import { inject, Injectable } from '@angular/core';
import { Engagement } from '../data-models/engagement';
import { map, switchMap } from 'rxjs/operators';
import { EngagementRespondent } from '../data-models/engagement-respondent';
import { EngagementMetric } from '../data-models/engagement-metric';
import { Respondent } from '../data-models/respondent';
import { Questionnaire } from '../data-models/questionnaire';
import { take } from 'rxjs/operators';
import { RespondentReference } from '../data-models/respondent-reference';
import { EngagementReference } from '../data-models/engagement-reference';
import { LeaverAnalysis } from '../data-models/leaver-analysis';
import { HttpClient } from '@angular/common/http';
import { GlobalsService } from './globals.service';
import { Database, equalTo, get, object, objectVal, off, onValue, orderByChild, push, query, ref, remove, set, update } from '@angular/fire/database';
import { Auth } from '@angular/fire/auth';
import { PushidService } from './pushid.service';
import { from, Observable } from 'rxjs';
import { snapshotChanges } from '@angular/fire/compat/database';

@Injectable({
  providedIn: 'root'
})
export class AssessmentService {


  sEndpoint: string = 'https://apis.zenithr.net/api/';//'https://apis.zenithr.net/api/';//
  sDevEndpoint: string = 'http://localhost:52619/api/';
  fbFunctionURL: string = '';

  pushIdService = inject(PushidService)


  constructor(private db: Database, public afAuth: Auth, private http: HttpClient, private globals: GlobalsService) {
    this.sEndpoint = this.globals.sEndpoint;
    this.fbFunctionURL = this.globals.fbFunctionURL;
  }


  createEngagementResList(request) {
    return this.http.post(this.sEndpoint + 'Assessment/CreateAssessmentResList', request);
  }

  updateEngagement(tenantKey: string, companyKey: string, engagement: Engagement) {
    const engagementRef = ref(this.db, `/TenantData/${tenantKey}/Assessments/${companyKey}/${engagement.key}`);
    return update(engagementRef, engagement);
  }

  fetchEngagements(tenantKey: string, companyKey: string): Observable<any[]> {
    const q = query(
      ref(this.db, `/TenantData/${tenantKey}/Assessments/${companyKey}`),
      orderByChild('isDeleted'),
      equalTo(false)
    );

    return new Observable<any[]>(subscriber => {
      const listener = onValue(q, snapshot => {
        const data = snapshot.val() || {};
        const results = Object.entries(data).map(([key, value]: any) => ({ key, ...value }));
        subscriber.next(results);
      }, error => subscriber.error(error));

      // Cleanup listener when unsubscribed
      return () => off(q, 'value', listener);
    });
  }

  fetchEngagementsOnce(tenantKey: string, companyKey: string) {
    const q = query(ref(this.db, `/TenantData/${tenantKey}/Assessments/${companyKey}`), orderByChild('isDeleted'), equalTo(false));
    return from(get(q)).pipe(
      map(snapshot => Object.entries(snapshot.val() || {}).map(([key, value]: any) => ({ key, ...value }))),
      take(1)
    );
  }

  fetchEngagement(tenantKey: string, companyKey: string, engagementKey: string) {
    const engagementRef = ref(this.db, `/TenantData/${tenantKey}/Assessments/${companyKey}/${engagementKey}`);
    return from(get(engagementRef)).pipe(
      map(snapshot => ({ key: snapshot.key, ...snapshot.val() })),
      take(1)
    );
  }

  fetchEngagementRespondentList(tenantKey: string, companyKey: string, engagementKey: string) {
    const listRef = ref(this.db, `/TenantData/${tenantKey}/AssessmentDetails/${companyKey}/${engagementKey}/AssessmentRespondentList`);
    return from(get(listRef)).pipe(
      map(snapshot => Object.entries(snapshot.val() || {}).map(([key, value]: any) => ({ key, ...value }))),
      take(1)
    );
  }

  fetchEngagementRespondent(tenantKey: string, companyKey: string, engagementKey: string, respondentKey: string) {
    const respondentRef = ref(this.db, `/TenantData/${tenantKey}/AssessmentDetails/${companyKey}/${engagementKey}/AssessmentRespondentList/${respondentKey}`);
    return from(get(respondentRef)).pipe(
      map(snapshot => ({ key: snapshot.key, ...snapshot.val() })),
      take(1)
    );
  }

  fetchRespondents(tenantKey: string, companyKey: string) {
    const listRef = ref(this.db, `/TenantData/${tenantKey}/RespondentList/${companyKey}`);
    return from(get(listRef)).pipe(
      map(snapshot => Object.entries(snapshot.val() || {}).map(([key, value]: any) => ({ key, ...value })))
    );
  }

  removeEngagementRespondent(tenantKey: string, companyKey: string, engagementKey: string, respondentKey: string) {
    const statusRef = ref(this.db, `/TenantData/${tenantKey}/AssessmentDetails/${companyKey}/${engagementKey}/AssessmentRespondentList/${respondentKey}`);
    return update(statusRef, { status: 'DELETED', actualStatus: 'DELETED' });
  }

  deleteEngagementRespondent(tenantKey: string, companyKey: string, engagementKey: string, respondentKey: string) {
    const respondentRef = ref(this.db, `/TenantData/${tenantKey}/AssessmentDetails/${companyKey}/${engagementKey}/AssessmentRespondentList/${respondentKey}`);
    return remove(respondentRef);
  }

  fetchEngagementMetrics(tenantKey: string, companyKey: string, engagementKey: string) {
    const metricsRef = ref(this.db, `/TenantData/${tenantKey}/AssessmentDetails/${companyKey}/${engagementKey}/AssessmentRespondentMetrics`);
    return new Observable<any[]>(subscriber => {
      const listener = onValue(metricsRef, snapshot => {
        const data = snapshot.val() || {};
        const results = Object.entries(data).map(([key, value]: any) => ({ key, ...value }));
        subscriber.next(results);
      }, error => subscriber.error(error));

      return () => off(metricsRef, 'value', listener);
    });
  }

  fetchPredefinedMetrics(tenantKey: string, companyKey: string, respondentKey: string) {
    const metricsRef = ref(this.db, `/TenantData/${tenantKey}/RespondentDetails/${companyKey}/${respondentKey}`);
    return from(get(metricsRef)).pipe(
      map(snapshot => Object.entries(snapshot.val() || {}).map(([key, value]: any) => ({ key, ...value }))),
      take(1)
    );
  }

  fetchRespondentFromPool(tenantKey: string, companyKey: string, respondentEmail: string) {
    const listRef = query(ref(this.db, `/TenantData/${tenantKey}/RespondentList/${companyKey}`), orderByChild('email'), equalTo(respondentEmail));
    return from(get(listRef)).pipe(
      map(snapshot => Object.entries(snapshot.val() || {}).map(([key, value]: any) => ({ key, ...value }))),
      take(1)
    );
  }

  updateSurveyRespondent(
    tenantKey: string,
    companyKey: string,
    engagementKey: string,
    engagementRespondent: EngagementRespondent,
    userKey?: string,
    sendEmail: boolean = false,
    companyName?: any[],
    engagement?: Engagement
  ) {
    const resps: EngagementRespondent[] = [engagementRespondent];

    resps.forEach((er) => {
      if (!er.key) {
        er.key = this.pushIdService.generatePushId();
        er.referenceKey = this.pushIdService.generatePushId();
        er.companyKey = companyKey;
        er.tenantKey = tenantKey;
      }
      er.createdBy = 'SELF';
    });

    const request = {
      SurveyType: 'ASM',
      TenantKey: tenantKey,
      CompanyKey: companyKey,
      SurveyKey: engagementKey,
      respondents: resps,
      sendEmail,
      CompanyName: companyName,
      surveyInfo: engagement,
    };

    return this.http
      .post(this.sEndpoint + 'Respondent/UpdateSurveyRespondent', request)
      .pipe(
        switchMap(() => {
          const engagementRespondent = resps[0];
          const path = `/TenantData/${tenantKey}/AssessmentDetails/${companyKey}/${engagementKey}/AssessmentRespondentList/${engagementRespondent.key}`;
          return update(ref(this.db, path), engagementRespondent);
        })
      );
  }

  updateEngagementRespondent(
    tenantKey: string,
    companyKey: string,
    engagementKey: string,
    engagementRespondent: EngagementRespondent
  ) {
    if (!engagementRespondent.key) {
      engagementRespondent.key = this.pushIdService.generatePushId();
      engagementRespondent.referenceKey = this.pushIdService.generatePushId();
      engagementRespondent.companyKey = companyKey;
      engagementRespondent.tenantKey = tenantKey;
    }

    const path = `/TenantData/${tenantKey}/AssessmentDetails/${companyKey}/${engagementKey}/AssessmentRespondentList/${engagementRespondent.key}`;
    return update(ref(this.db, path), engagementRespondent);
  }


  updateEngagementReference(respondentReference: RespondentReference) {
    return update(ref(this.db, '/AssessmentReferences/' + respondentReference.key), respondentReference);
  }

  updateEngagementMetrics(tenantKey: string, companyKey: string, engagementKey: string, metrics: EngagementMetric[]) {
    metrics.forEach(metric => {
      if (!metric.key) metric.key = push(ref(this.db)).key!;
    });
    const path = `/TenantData/${tenantKey}/AssessmentDetails/${companyKey}/${engagementKey}/AssessmentRespondentMetrics/`;
    return set(ref(this.db, path), metrics);
  }

  updateRespondentList(tenantKey: string, companyKey: string, respondent: Respondent) {
    return update(ref(this.db, `/TenantData/${tenantKey}/RespondentList/${companyKey}/${respondent.key}`), respondent);
  }

  removeRespondentFromPool(tenantKey: string, companyKey: string, respondentKey: string) {
    return remove(ref(this.db, `/TenantData/${tenantKey}/RespondentList/${companyKey}/${respondentKey}`));
  }

  fetchAssessmentQuestionnaires() {
    const q = query(ref(this.db, '/Questionnaires/'), orderByChild('isAssessment'));
    return from(get(q)).pipe(
      map(snapshot => {
        const data: Questionnaire[] = [];
        snapshot.forEach(child => {
          if (child.val()?.isAssessment) data.push({ key: child.key!, ...child.val() });
        });
        return data;
      }),
      take(1)
    );
  }

  updateEngagementRespondentSurvey(tenantKey: string, companyKey: string, engagementKey: string, analysis: LeaverAnalysis) {
    return update(ref(this.db, `/AssessmentAnalysis/${tenantKey}/${companyKey}/${engagementKey}/${analysis.key}`), analysis);
  }

  fetchAssessmentTemplates(tenantKey: string, companyKey: string) {
    return from(get(ref(this.db, `/TenantData/${tenantKey}/AssessmentTemplates/${companyKey}/`))).pipe(
      map(snapshot => {
        const data: Questionnaire[] = [];
        snapshot.forEach((child: any) => {
          data.push({ key: child.key!, ...child.val() });
        });
        return data;
      })
    );
  }

  fetchAssessmentTemplate(
    tenantKey: string,
    companyKey: string,
    templateKey: string
  ) {
    const path = `/TenantData/${tenantKey}/AssessmentTemplates/${companyKey}/${templateKey}`;

    const dbRef = ref(this.db, path);

    return objectVal<Questionnaire>(dbRef, {
      keyField: 'key'
    }).pipe(take(1));
  }

  removeAssessmentTemplate(tenantKey: string, companyKey: string, surveyTemplate: Questionnaire) {
    return remove(ref(this.db, `/TenantData/${tenantKey}/AssessmentTemplates/${companyKey}/${surveyTemplate.key}`));
  }

  updateAssessmentTemplate(tenantKey: string, companyKey: string, surveyTemplate: Questionnaire) {
    return update(ref(this.db, `/TenantData/${tenantKey}/AssessmentTemplates/${companyKey}/${surveyTemplate.key}`), surveyTemplate);
  }

  fetchEngagementReference(engagementReference: string) {
    return from(get(ref(this.db, `/AssessmentReferences/${engagementReference}`))).pipe(
      map(snapshot => ({ key: snapshot.key!, ...snapshot.val() } as EngagementReference)),
      take(1)
    );
  }

  removeEngagement(tenantKey: string, companyKey: string, engagementKey: string) {
    return set(ref(this.db, `/TenantData/${tenantKey}/Assessments/${companyKey}/${engagementKey}/isDeleted/`), true);
  }

  removeEngagementRespondentList(tenantKey: string, companyKey: string, engagementKey: string) {
    return remove(ref(this.db, `/TenantData/${tenantKey}/AssessmentDetails/${companyKey}/${engagementKey}`));
  }

  removeEngagementRespondentAnalysis(tenantKey: string, companyKey: string, engagementKey: string, respondentKey: string) {
    return remove(ref(this.db, `/AssessmentAnalysis/${tenantKey}/${companyKey}/${engagementKey}/${respondentKey}`));
  }

  addRespondent(request) {
    return this.http.post(this.sEndpoint + 'Assessment/AddRespondent', request);
  }

  addRespondentFromLandingPage(request) {
    return this.http.post(this.sEndpoint + 'Assessment/AddRespondentFromLandingPage', request);
  }

  fetchOverviewAnalysis(request) {
    return this.http.post(this.sEndpoint + 'Assessment/GetOverviewAnalysis', request);
  }

  migrationOverviewAnalysis(request) {
    return this.http.post(this.sEndpoint + 'Assessment/MigrationGetOverviewAnalysis', request);
  }

  fetchAssessmentRespondents(request) {
    return this.http.post(this.sEndpoint + 'Assessment/FetchAssessmentRespondents', request);
  }

  fetchAllRespondents(request) {
    return this.http.post(this.sEndpoint + 'Assessment/FetchAllRespondents', request);
  }


  fetchSummaryAnalysis(request) {
    return this.http.post(this.sEndpoint + 'Assessment/FetchOverallSummary', request);
  }


  fetchHierarchyCount(request) {
    return this.http.post(this.sEndpoint + 'Assessment/GetCountInHirarchy', request);
  }

  fetchCommentAnalysis(request) {
    return this.http.post(this.sEndpoint + 'Assessment/GetCommentAnalysis', request);
  }

  insertEngagementTemplate(request) {
    return this.http.post(this.sEndpoint + 'SurveyDetails/SurveyGoLive', request);
  }

  updateEvaluatorLimit(request) {
    return this.http.post(this.sEndpoint + 'Assessment/UpdateEvaluator', request);
  }


  fetchSurveyRespondent(request) {
    return this.http.post(this.sEndpoint + 'Assessment/FetchSurveyRespondets', request);
  }

  updateFinalSurvey(request) {
    return this.http.post(this.sEndpoint + 'UpdateSurveyDetails', request);
  }


  removeRespondents(request) {
    return this.http.post(this.sEndpoint + 'Assessment/RemoveRespondents', request);
  }


  fetchAssessmentList(request) {
    return this.http.post(this.sEndpoint + 'Assessment/FetchAllRespondents', request);
  }


  insertSurveyAnswers(request) {
    return this.http.post(this.sEndpoint + 'Assessment/SubmitAnswers', request);
  }

  updateRespondent(request) {
    return this.http.post(this.sEndpoint + 'Assessment/UpdateRespondent', request);
  }
  fetchEngagementAnalysis(tenantKey: string, companyKey: string, engagementKey: string) {
    const path = `/AssessmentAnalysis/${tenantKey}/${companyKey}/${engagementKey}/`;
    return from(get(ref(this.db, path))).pipe(
      map(snapshot => {
        const data: LeaverAnalysis[] = [];
        snapshot.forEach(child => {
          data.push({ key: child.key!, ...child.val() });
        });
        return data;
      }),
      take(1)
    );
  }

  checkBalanceAndCreditAssessment(request) {
    return this.http.post(this.fbFunctionURL + 'creditAssessment', request);
  }

  getPreviousScores(request) {
    return this.http.post(this.sEndpoint + 'Assessment/GetPreviousScores', request);
  }
  closeCandidate(request) {
    return this.http.post(this.sEndpoint + 'Assessment/CloseCandidate', request);
  }
  getComparisonExcel(request) {
    return this.http.post(this.sEndpoint + 'Assessment/GetExcelComparison', request);
  }
  getProgressExcel(request) {
    return this.http.post(this.sEndpoint + 'Assessment/GetExcelProgress', request);
  }
  registerAndFetchCandidateHistory(request) {
    return this.http.post(this.fbFunctionURL + 'registerAndFetchCandidateHistory', request);
  }

  downloadComments(surveyKey: string, tenantKey: string, companyKey: string, candidateKey: string = null) {
    const request = {
      surveyKey: surveyKey,
      tenantKey: tenantKey,
      companyKey: companyKey,
      candidateKey: candidateKey
    }

    return this.http.post(this.sEndpoint + 'Assessment/DownloadComments', request);
  }


  CloneSurveyRespondents(request) {
    return this.http.post(this.sEndpoint + 'Assessment/CloneSurvey', request);
  }

  fetchRespondentListByCriteria(request) {
    return this.http.get(this.sEndpoint + `Assessment/GetLiveAnalysis?TenantKey=${request.TenantKey}&CompanyKey=${request.CompanyKey}&SurveyKey=${request.SurveyKey}&Criteria=${request.Criteria}&Type=${request.Type}`);
  }

}
